/**
 * Shared meeting-intelligence / recapStatus merge helpers.
 * Used by supergraph list normalization, mapNodeToMeeting, and notetaker recap UI.
 */

const MEETING_INTELLIGENCE_STATUS_PRIORITY = {
  COMPLETED: 100,
  SUMMARIZING: 50,
  MEETING_ENDED: 40,
  MEETING_IN_PROGRESS: 30,
  PRE_JOIN: 20,
  SCHEDULED: 10,
}

/**
 * Normalize meeting intelligence enum values to SCREAMING_SNAKE_CASE.
 * Supergraph `recapStatus` and tracked-meetings APIs return PascalCase (e.g. `Completed`,
 * `HostRecordingPermissionDenied`); search/cmd payloads use SCREAMING_SNAKE_CASE.
 */
const normalizeMeetingIntelligenceEnumValue = (value) => {
  if (!value || typeof value !== 'string') return value

  const trimmed = value.trim()
  if (trimmed === trimmed.toUpperCase()) return trimmed

  return trimmed
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toUpperCase()
}

/** Normalize meeting intelligence status to SCREAMING_SNAKE_CASE {@link EventStatus} shape. */
const normalizeMeetingIntelligenceStatus = normalizeMeetingIntelligenceEnumValue

/** Normalize meeting intelligence error codes to SCREAMING_SNAKE_CASE. */
const normalizeMeetingIntelligenceError = normalizeMeetingIntelligenceEnumValue

const meetingIntelligenceStatusPriority = (status) => (
  MEETING_INTELLIGENCE_STATUS_PRIORITY[normalizeMeetingIntelligenceStatus(status)] ?? 0
)

const isMeetingIntelligencePayload = (value) => (
  value != null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0
)

/**
 * Merge meeting intelligence objects, preferring the more advanced recap lifecycle status
 * (e.g. Completed over Summarizing or Error).
 */
export const mergeMeetingIntelligence = (prev, next) => {
  if (!prev) return next
  if (!next) return prev

  const prevPriority = meetingIntelligenceStatusPriority(prev.status)
  const nextPriority = meetingIntelligenceStatusPriority(next.status)
  const [preferred, other] = nextPriority >= prevPriority ? [next, prev] : [prev, next]

  const merged = { ...other, ...preferred }

  // Winning payloads often omit cleared fields (e.g. `error: null`). Drop stale values
  // from the superseded payload so recap UI does not treat them as still active.
  if (nextPriority !== prevPriority && !Object.prototype.hasOwnProperty.call(preferred, 'error')) {
    delete merged.error
  }

  return merged
}

/**
 * Merge meeting intelligence objects from one or more API shapes.
 */
export const coalesceMeetingIntelligence = (...sources) => {
  const payloads = sources.filter(isMeetingIntelligencePayload)
  if (payloads.length === 0) return undefined

  return payloads.reduce((merged, source) => mergeMeetingIntelligence(merged, source))
}

/**
 * Read meeting intelligence from list/detail payloads. Supergraph list nodes may expose
 * `recapStatus` while older search payloads use `meetingIntelligence` / `meeting_intelligence`.
 */
export const getMeetingIntelligenceFromEvent = (event) => {
  if (!event) return undefined

  return coalesceMeetingIntelligence(
    event.meetingIntelligence,
    event.recapStatus,
    event.meeting_intelligence,
  )
}

/** Normalized recap status for notetaker/recap UI state machines. */
export const getMeetingIntelligenceStatus = (event) => (
  normalizeMeetingIntelligenceStatus(getMeetingIntelligenceFromEvent(event)?.status)
)

/** Normalized recap error code for notetaker/recap UI state machines. */
export const getMeetingIntelligenceError = (event) => (
  normalizeMeetingIntelligenceError(getMeetingIntelligenceFromEvent(event)?.error)
)

/**
 * Merge two list-row meeting events, coalescing recap status from every intelligence field.
 */
export const mergeMeetingListEvent = (base, incoming) => {
  if (!incoming) return base
  if (!base) return incoming

  const meetingIntelligence = coalesceMeetingIntelligence(
    base.meetingIntelligence,
    base.recapStatus,
    base.meeting_intelligence,
    incoming.meetingIntelligence,
    incoming.recapStatus,
    incoming.meeting_intelligence,
  )

  return {
    ...base,
    ...incoming,
    ...(meetingIntelligence ? { meetingIntelligence } : {}),
    source: incoming.source ?? base.source,
  }
}
